// @ts-check
import { gql, useQuery } from '@apollo/client';
import React from 'react';
import { Box, Text } from '~/design-system';
import FieldsTableErrors from '../../../../components/Validation/FieldTableErrors';
import FieldsTableValidation from '../../../../components/Validation/FieldsTableValidation';
import ValidationSkeleton from '../../../../components/Validation/ValidationSkeleton';

/**
 * The props that this component needs.
 * @typedef {object} ValidateImportStepProps
 * @property {string} props.redirectToIfValid - the link to redirect if there is no errors
 * @property {Object[]} props.masterListColumns - the master list columns to this import
 */

/**
 * Renders the page where import is validated
 * @param {ReturnType<useIdentifierValidation> & ValidateImportStepProps} props
 */
export default function IdentifierValidation({
  importData,
  isValid,
  masterListColumns,
  loading,
  redirectToIfValid,
  ...rest
}) {
  const [columnErrorSelected, setColumnErrorSelected] = React.useState(null);
  if (loading) return <ValidationSkeleton />;
  // if (isValid && redirectToIfValid) return <Redirect to={redirectToIfValid} />;
  const {
    sourceFields,
    identifierFields = [],
    dataRowIndex,
    childProductCreationImportId,
  } = importData;
  const errorCodes = identifierFields.reduce((acc, { errors }) => {
    errors.forEach(({ code }) => {
      acc[code] = true;
    });
    return acc;
  }, {});
  const canAllowCreation =
    Object.keys(errorCodes).length === 1 && errorCodes.required;

  return (
    <Box
      sx={{
        textAlign: 'center',
        marginTop: 2,
        padding: '0px 15% 30px 15%',
      }}
      {...rest}
    >
      <FieldsTableValidation
        childProductCreationImportId={childProductCreationImportId}
        onMissingRequired={
          <Text variant="smallest">
            Products without specified ID will be created and added to
            Portfolio.
          </Text>
        }
        sourceFields={sourceFields}
        bottlebooksFields={identifierFields}
        onShowColErrors={(col) => setColumnErrorSelected(col)}
        masterListColumns={masterListColumns}
        isValid={isValid}
      />
      {!isValid && columnErrorSelected && (
        <FieldsTableErrors
          canAllowCreation={canAllowCreation}
          key={columnErrorSelected.key}
          sx={{ marginTop: 5, marginBottom: 6 }}
          sourceFields={sourceFields}
          bottlebooksFields={identifierFields}
          column={columnErrorSelected}
          dataRowIndex={dataRowIndex}
          masterListColumns={masterListColumns}
        />
      )}
    </Box>
  );
}

/**
 * The business logic. The route is then responsible for wiring up the returned props to the view component.
 * @param {Object} props
 * @param {String} props.workspaceId
 * @param {String} props.importId
 * @returns
 */
export function useIdentifierValidation({ workspaceId, importId }) {
  const { data, loading } = useQuery(QUERY, {
    variables: { workspaceId, importId },
  });

  return {
    loading,
    ...afterQuery(data),
  };
}

function afterQuery(data) {
  const importData = data?.workspace?.dataImport;
  const isValid = !Boolean(
    importData?.identifierFields?.find(({ errors }) => errors?.length > 0)
  );
  return {
    importData,
    isValid,
  };
}

const QUERY = gql`
  query Validation($workspaceId: ID!, $importId: ID!) {
    workspace(workspaceId: $workspaceId) {
      workspaceId
      dataImport(importId: $importId) {
        importId
        childProductCreationImportId
        dataRowIndex
        identifierFields {
          bottlebooksFieldName
          importFieldName
          errors {
            index
            code
            value
          }
        }
      }
    }
  }
`;
